import {
    buildCommonSafetyDeclarationLines,
    wrapBodyWithCommonSafetyTemplate
} from "./common-safety-template.js";

function numOrZero(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

function toStIdentifier(value, fallback) {
    const base = String(value || "")
        .replace(/[^A-Za-z0-9_]+/g, "_")
        .replace(/^_+|_+$/g, "");
    const normalized = base || fallback;
    return /^[A-Za-z_]/.test(normalized) ? normalized : "_" + normalized;
}

function buildBooleanAssignment(action) {
    return action === "RESET" || action === "OFF" ? "FALSE" : "TRUE";
}

function formatTimeLiteral(seconds) {
    const n = Math.max(numOrZero(seconds), 0);
    if (n === 0) return "T#0S";
    return `T#${n}S`;
}

function pushTransition(lines, nextStep, stepVar, indent = "        ") {
    if (nextStep == null) {
        lines.push(`${indent}// TODO: 接続先が未定義です`);
        return;
    }
    lines.push(`${indent}${stepVar} := ${nextStep};`);
}

const PLCOPEN_INITIAL_FB_SET = Object.freeze([
    "MC_Power",
    "MC_Home",
    "MC_MoveAbsolute",
    "MC_MoveRelative",
    "MC_MoveVelocity",
    "MC_Stop",
    "MC_Reset"
]);

function buildPlcopenProfile(vendorTarget = "") {
    return {
        vendorTarget: vendorTarget || "generic-st",
        standardFamilies: ["motionPart1", "motionPart6FluidPower"],
        supportedFbNames: [...PLCOPEN_INITIAL_FB_SET],
        vendorMappingPolicy: "keepStandardFbNamesInIr"
    };
}

function buildSignalIdentifierMap(ir) {
    const map = new Map();
    const signals = Array.isArray(ir?.signals) ? ir.signals : [];
    for (const sig of signals) {
        if (!sig?.id) continue;
        map.set(sig.id, sig.iecName || sig.id);
    }
    return map;
}

function getSignalIdentifier(signalMap, signalId = "") {
    return signalMap.get(signalId) || signalId;
}

function collectOutputSignalIdentifiers(ir, signalMap) {
    const seen = new Set();
    const identifiers = [];
    const signals = Array.isArray(ir?.signals) ? ir.signals : [];
    for (const signal of signals) {
        if (signal?.direction !== "output" || !signal?.id) continue;
        const identifier = getSignalIdentifier(signalMap, signal.id);
        if (!identifier || seen.has(identifier)) continue;
        seen.add(identifier);
        identifiers.push(identifier);
    }
    return identifiers;
}

function normalizeValveOutputState(value, fallback = "hold") {
    const normalized = String(value || "").trim();
    if (normalized === "on" || normalized === "off" || normalized === "hold") {
        return normalized;
    }
    return fallback;
}

function normalizePneumaticValveMode(actuator = {}) {
    return actuator?.pneumaticValveMode === "singleSolenoid" ? "singleSolenoid" : "doubleSolenoid";
}

function buildActuatorValveStateEntries(actuator = {}, stateKey = "emergency", signalMap = new Map()) {
    if (actuator?.actuatorType !== "pneumaticCylinder" && actuator?.actuatorType !== "cylinderIo") return [];
    const valveModel = actuator?.valveModel && typeof actuator.valveModel === "object" ? actuator.valveModel : {};
    const solenoidConfig = actuator?.solenoidConfig && typeof actuator.solenoidConfig === "object" ? actuator.solenoidConfig : {};
    const stateRow = valveModel?.stateTable?.[stateKey] && typeof valveModel.stateTable[stateKey] === "object"
        ? valveModel.stateTable[stateKey]
        : {};
    const valveMode = normalizePneumaticValveMode(actuator);
    if (valveMode === "singleSolenoid") {
        const signalId = String(valveModel?.singleOutput || solenoidConfig?.singleOutput || "").trim();
        if (!signalId) return [];
        return [{
            signalName: getSignalIdentifier(signalMap, signalId),
            state: normalizeValveOutputState(stateRow?.single, "hold")
        }];
    }
    const entries = [];
    const positiveSignalId = String(valveModel?.positiveOutput || solenoidConfig?.positiveOutput || "").trim();
    const negativeSignalId = String(valveModel?.negativeOutput || solenoidConfig?.negativeOutput || "").trim();
    if (positiveSignalId) {
        entries.push({
            signalName: getSignalIdentifier(signalMap, positiveSignalId),
            state: normalizeValveOutputState(stateRow?.positive, "hold")
        });
    }
    if (negativeSignalId) {
        entries.push({
            signalName: getSignalIdentifier(signalMap, negativeSignalId),
            state: normalizeValveOutputState(stateRow?.negative, "hold")
        });
    }
    return entries;
}

function buildActuatorValveAssignments(actuator = {}, stateKey = "emergency", signalMap = new Map()) {
    return buildActuatorValveStateEntries(actuator, stateKey, signalMap)
        .filter((entry) => entry.signalName && entry.state !== "hold")
        .map((entry) => ({
            signalName: entry.signalName,
            stateValue: entry.state === "on" ? "TRUE" : "FALSE"
        }));
}

function buildSafetyOutputAssignments(ir, signalMap, stateKey = "emergency") {
    const assignments = new Map(
        collectOutputSignalIdentifiers(ir, signalMap).map((signalName) => [signalName, "FALSE"])
    );
    const actuators = Array.isArray(ir?.actuators) ? ir.actuators : [];
    for (const actuator of actuators) {
        for (const entry of buildActuatorValveStateEntries(actuator, stateKey, signalMap)) {
            if (!entry.signalName) continue;
            if (entry.state === "hold") {
                assignments.delete(entry.signalName);
                continue;
            }
            assignments.set(entry.signalName, entry.state === "on" ? "TRUE" : "FALSE");
        }
    }
    return Array.from(assignments.entries()).map(([signalName, stateValue]) => ({ signalName, stateValue }));
}

function pushOutputAssignments(lines, assignments = [], indent = "        ") {
    for (const assignment of Array.isArray(assignments) ? assignments : []) {
        if (!assignment?.signalName || !assignment?.stateValue) continue;
        lines.push(`${indent}${assignment.signalName} := ${assignment.stateValue};`);
    }
}

function getActiveProfileConfig(snapshot = {}) {
    return snapshot?.assistant?.plcProfile || {
        codegen: {
            declarationBlock: "VAR_GLOBAL",
            includeAddressesInDeclarations: true,
            preferIoMapping: false,
            defaultBlinkMs: 500
        },
        ioPolicy: {
            allowAtOnPouInterface: false,
            preferGlobalIoVariables: true
        }
    };
}

function collectGlobalSignalDeclarations(ir, profile = {}) {
    const signals = Array.isArray(ir?.signals) ? ir.signals : [];
    const includeAddresses = profile?.codegen?.includeAddressesInDeclarations !== false;
    return signals
        .filter((sig) => sig?.iecName)
        .map((sig) => ({
            iecName: sig.iecName,
            address: includeAddresses ? (sig.address || "") : "",
            dataType: sig.dataType || "BOOL"
        }));
}

function renderPlcopenMotionStep(lines, step, dialectMeta) {
    const action = step.action || {};
    const fbName = action.plcopen?.fbName || "";
    const axisRef = step.actuator?.axisRef || "";
    const instanceName = toStIdentifier(`fb_${step.actuator?.id || "Actuator"}_${action.id || step.step}`, `fbStep${step.step}`);
    const executeSignal = action.command?.executeSignal || "TRUE";
    const params = action.parameters || {};
    const status = action.status || {};
    const callArgs = [
        axisRef ? `Axis := ${axisRef}` : "",
        `Execute := ${executeSignal}`,
        params.position ? `Position := ${params.position}` : "",
        params.velocity ? `Velocity := ${params.velocity}` : ""
    ].filter(Boolean);

    lines.push(`        // PLCopen ${action.plcopen?.family || "motion"}: ${step.actuator?.name || "-"} / ${action.name || "-"} / ${fbName || "-"}`);
    if (!fbName) {
        lines.push("        // TODO: PLCopen FB名が未設定です");
        pushTransition(lines, step.transitions.next, dialectMeta.stepVar);
        return;
    }
    if (!axisRef) {
        lines.push("        // TODO: Axis参照が未設定です");
    }
    lines.push(`        ${instanceName}(${callArgs.join(", ")});`);
    if (status.busySignal) lines.push(`        ${status.busySignal} := ${instanceName}.Busy;`);
    if (status.doneSignal) lines.push(`        ${status.doneSignal} := ${instanceName}.Done;`);
    if (status.errorSignal) lines.push(`        ${status.errorSignal} := ${instanceName}.Error;`);
    if (status.errorIdSignal) lines.push(`        ${status.errorIdSignal} := ${instanceName}.ErrorID;`);
    lines.push(`        IF ${instanceName}.Done THEN`);
    pushTransition(lines, step.transitions.next, dialectMeta.stepVar, "            ");
    lines.push(`        ELSIF ${instanceName}.Error THEN`);
    lines.push("            // TODO: PLCopen FBエラー処理");
    if (step.transitions.timeout != null) {
        pushTransition(lines, step.transitions.timeout, dialectMeta.stepVar, "            ");
    } else {
        lines.push(`            ${dialectMeta.stepVar} := 9999; // エラー用ダミーステップ`);
    }
    lines.push("        END_IF;");
}

function collectPlcopenFbDeclarations(steps = []) {
    const declarations = [];
    const seen = new Set();
    for (const step of steps) {
        const action = step?.action || {};
        const fbName = action.plcopen?.fbName || "";
        if (!fbName) continue;
        const instanceName = toStIdentifier(`fb_${step.actuator?.id || "Actuator"}_${action.id || step.step}`, `fbStep${step.step}`);
        if (seen.has(instanceName)) continue;
        seen.add(instanceName);
        declarations.push({ instanceName, fbName });
    }
    return declarations;
}

function renderMotionStep(lines, step, dialectMeta, helpers, signalMap, actuatorById = new Map()) {
    const axis = step.axis || null;
    const motion = step.motion || null;
    const getAxisDistanceUnit = helpers.getAxisDistanceUnit || (() => "-");
    const getAxisSpeedUnit = helpers.getAxisSpeedUnit || (() => "-");
    const actuatorConfig = step?.actuator?.id ? actuatorById.get(step.actuator.id) || null : null;

    if (
        step.actuator &&
        (step.actuator.controlMethod === "plcopenMotion" || step.actuator.controlMethod === "plcopenFluidPower")
    ) {
        renderPlcopenMotionStep(lines, step, dialectMeta);
        return;
    }

    if (step.actuator && step.actuator.controlMethod === "discreteIo") {
        lines.push(`        // Actuator: ${step.actuator.name || "-"} (${step.actuator.id}), Action: ${step.action?.name || "-"}`);
        if (Array.isArray(step.commands)) {
            for (const cmd of step.commands) {
                const stateVal = cmd.type === "setOutput" ? "TRUE" : "FALSE";
                lines.push(`        ${getSignalIdentifier(signalMap, cmd.signalId)} := ${stateVal};`);
            }
        }

        const completionMode = step.completionMode || "sensor";
        let condText = "TRUE";
        if (completionMode === "sensor" && Array.isArray(step.doneConditions) && step.doneConditions.length > 0) {
            condText = step.doneConditions.map(cond => {
                const op = cond.operator || "==";
                const rightHand = cond.value === false ? "FALSE" : "TRUE";
                const stOp = op === "==" ? "=" : op;
                return `${getSignalIdentifier(signalMap, cond.signalId)} ${stOp} ${rightHand}`;
            }).join(" AND ");
        }

        if (completionMode === "ignore") {
            pushTransition(lines, step.transitions.next, dialectMeta.stepVar);
            return;
        }

        if (completionMode === "timer" && numOrZero(step.timeoutSec) > 0) {
            const timerName = `ActTimer_${step.step}`;
            lines.push(`        ${timerName}(IN := TRUE, PT := ${formatTimeLiteral(step.timeoutSec)});`);
            lines.push(`        IF ${timerName}.Q THEN`);
            pushTransition(lines, step.transitions.next, dialectMeta.stepVar, "            ");
            lines.push("        END_IF;");
            return;
        }

        if (numOrZero(step.timeoutSec) > 0) {
            const timerName = `ActTimer_${step.step}`;
            lines.push(`        ${timerName}(IN := NOT (${condText}), PT := ${formatTimeLiteral(step.timeoutSec)});`);
            lines.push(`        IF ${condText} THEN`);
            pushTransition(lines, step.transitions.next, dialectMeta.stepVar, "            ");
            lines.push(`        ELSIF ${timerName}.Q THEN`);
            pushOutputAssignments(
                lines,
                buildActuatorValveAssignments(actuatorConfig, "emergency", signalMap),
                "            "
            );
            if (step.transitions.timeout != null) {
                pushTransition(lines, step.transitions.timeout, dialectMeta.stepVar, "            ");
            } else {
                lines.push(`            ${dialectMeta.stepVar} := 9999; // エラー用ダミーステップ`);
            }
            lines.push("        END_IF;");
        } else {
            lines.push(`        IF ${condText} THEN`);
            pushTransition(lines, step.transitions.next, dialectMeta.stepVar, "            ");
            lines.push("        END_IF;");
        }
        return;
    }

    lines.push(`        // ${step.axisName || "-"} / ${step.motionName || "-"}`);
    lines.push(
        `        // Distance=${motion?.distance ?? "-"} ${axis ? getAxisDistanceUnit(axis) : "-"}, MaxSpeed=${motion?.maxSpeed ?? "-"} ${axis ? getAxisSpeedUnit(axis) : "-"}`
    );
    lines.push(`        ${step.commandName}();`);
    lines.push(`        IF ${step.doneCondition} THEN`);
    if (numOrZero(motion?.waitTime) > 0) {
        lines.push(`            // Wait ${numOrZero(motion.waitTime).toFixed(2)}s before next step`);
    }
    pushTransition(lines, step.transitions.next, dialectMeta.stepVar, "            ");
    lines.push("        END_IF;");
}

function renderConditionStep(lines, step, dialectMeta) {
    const condExpr = step.conditionExpression || `Cond_${step.step}`;
    if (step.timeoutSec > 0 && step.transitions.timeout != null) {
        lines.push(`        ${step.timerName}(IN := NOT (${condExpr}), PT := ${formatTimeLiteral(step.timeoutSec)});`);
    }
    lines.push(`        IF ${condExpr} THEN`);
    pushTransition(lines, step.transitions.true, dialectMeta.stepVar, "            ");
    if (step.timeoutSec > 0 && step.transitions.timeout != null) {
        lines.push(`        ELSIF ${step.timerName}.Q THEN`);
        pushTransition(lines, step.transitions.timeout, dialectMeta.stepVar, "            ");
        lines.push("        ELSE");
        lines.push(`            ${step.timerName}(IN := TRUE, PT := ${formatTimeLiteral(step.timeoutSec)});`);
        lines.push("        END_IF;");
        return;
    }
    if (step.transitions.false != null) {
        lines.push("        ELSE");
        pushTransition(lines, step.transitions.false, dialectMeta.stepVar, "            ");
        lines.push("        END_IF;");
        return;
    }
    lines.push("        END_IF;");
}

function renderSignalOutputStep(lines, step, dialectMeta, signalMap) {
    lines.push(`        ${getSignalIdentifier(signalMap, step.signalName) || step.signalIdent} := ${buildBooleanAssignment(step.action)};`);
    pushTransition(lines, step.transitions.next, dialectMeta.stepVar);
}

function renderWaitStep(lines, step, dialectMeta) {
    lines.push(`        ${step.timerName}(IN := TRUE, PT := ${formatTimeLiteral(step.durationSec)});`);
    lines.push(`        IF ${step.timerName}.Q THEN`);
    lines.push(`            ${step.timerName}(IN := FALSE, PT := ${formatTimeLiteral(step.durationSec)});`);
    pushTransition(lines, step.transitions.next, dialectMeta.stepVar, "            ");
    lines.push("        END_IF;");
}

function renderSubprocessStep(lines, step, dialectMeta) {
    lines.push(`        ${step.timerName}(IN := TRUE, PT := ${formatTimeLiteral(step.durationSec)});`);
    lines.push(`        IF ${step.timerName}.Q THEN`);
    lines.push(`            ${step.timerName}(IN := FALSE, PT := ${formatTimeLiteral(step.durationSec)});`);
    pushTransition(lines, step.transitions.next, dialectMeta.stepVar, "            ");
    lines.push("        END_IF;");
}

function renderErrorStep(lines, step) {
    lines.push(`        ${step.errorIdent} := TRUE; // ${step.errorMessage}`);
    lines.push("        // TODO: add alarm reset / acknowledge flow");
}

function renderEndStep(lines, step, dialectMeta) {
    lines.push(`        // ${step.label || "終了"} (${step.result || "normal"})`);
    if (step.transitions?.next != null) {
        pushTransition(lines, step.transitions.next, dialectMeta.stepVar);
    } else {
        lines.push(`        ${dialectMeta.stepVar} := 0;`);
    }
}

function buildStBodyLines(ir, dialectMeta, helpers = {}) {
    const steps = Array.isArray(ir.steps) ? ir.steps : [];
    const signalMap = buildSignalIdentifierMap(ir);
    const actuatorById = new Map(
        (Array.isArray(ir?.actuators) ? ir.actuators : [])
            .filter((actuator) => actuator?.id)
            .map((actuator) => [actuator.id, actuator])
    );
    const lines = [];
    lines.push(`CASE ${dialectMeta.stepVar} OF`);
    lines.push("    0:");
    lines.push(`        IF ${dialectMeta.startSignal} THEN`);
    lines.push(`            ${dialectMeta.stepVar} := ${ir.startStep || (steps[0]?.step ?? 900)};`);
    lines.push("        END_IF;");
    lines.push("");

    for (const step of steps) {
        lines.push(`    ${step.step}:`);
        if (step.nodePath) {
            lines.push(`        // [Path: ${step.nodePath}]`);
        }
        switch (step.type) {
            case "motion":
                renderMotionStep(lines, step, dialectMeta, helpers, signalMap, actuatorById);
                break;
            case "condition":
                renderConditionStep(lines, step, dialectMeta);
                break;
            case "signal_output":
                renderSignalOutputStep(lines, step, dialectMeta, signalMap);
                break;
            case "wait":
                renderWaitStep(lines, step, dialectMeta);
                break;
            case "subprocess":
                renderSubprocessStep(lines, step, dialectMeta);
                break;
            case "error":
                renderErrorStep(lines, step);
                break;
            case "end":
                renderEndStep(lines, step, dialectMeta);
                break;
            case "start":
                pushTransition(lines, step.transitions.next, dialectMeta.stepVar);
                break;
            default:
                lines.push(`        // Unsupported node type: ${step.type}`);
                pushTransition(lines, step.transitions?.next ?? null, dialectMeta.stepVar);
                break;
        }
        lines.push("");
    }

    lines.push("END_CASE;");
    return lines;
}

export function buildLocalStDraftSectionsFromSnapshot(snapshot, dialectMeta, helpers = {}) {
    const ir = snapshot?.generationIr || { steps: [], startStep: 0 };
    const steps = Array.isArray(ir.steps) ? ir.steps : [];
    const profile = getActiveProfileConfig(snapshot);
    const signalMap = buildSignalIdentifierMap(ir);
    const bodyLines = buildStBodyLines(ir, dialectMeta, helpers);
    return {
        pou: {
            name: "Main",
            type: "program",
            language: "ST"
        },
        plcopenProfile: buildPlcopenProfile(ir.vendorTarget || snapshot?.flowContext?.vendorTarget || snapshot?.assistant?.dialect),
        globalSignals: collectGlobalSignalDeclarations(ir, profile),
        declarationBlock: profile?.codegen?.declarationBlock || "VAR_GLOBAL",
        profile,
        fbInstances: collectPlcopenFbDeclarations(steps),
        safetyDeclarations: buildCommonSafetyDeclarationLines(),
        bodyLines: wrapBodyWithCommonSafetyTemplate(bodyLines, {
            stepVar: dialectMeta.stepVar,
            outputSignals: collectOutputSignalIdentifiers(ir, signalMap),
            emergencyOutputAssignments: buildSafetyOutputAssignments(ir, signalMap, "emergency")
        })
    };
}

export function buildLocalStDraftFromSnapshot(snapshot, dialectMeta, helpers = {}) {
    const sections = buildLocalStDraftSectionsFromSnapshot(snapshot, dialectMeta, helpers);
    const lines = [];
    lines.push(`(* Auto-generated draft for ${dialectMeta.label} *)`);
    lines.push(`(* ${dialectMeta.comment} *)`);
    if (sections.profile?.codegen?.preferIoMapping) {
        lines.push("(* I/O Mapping 優先プロファイル: 物理アドレスはデバイスの I/O Mapping で割り付ける前提 *)");
    }
    if (sections.globalSignals.length > 0) {
        lines.push(sections.declarationBlock || "VAR_GLOBAL");
        for (const sig of sections.globalSignals) {
            const address = sig.address ? ` AT ${sig.address}` : "";
            lines.push(`    ${sig.iecName}${address} : ${sig.dataType};`);
        }
        lines.push("END_VAR");
        lines.push("");
    }
    if (sections.fbInstances.length > 0 || sections.safetyDeclarations.length > 0) {
        lines.push("VAR");
        for (const decl of sections.fbInstances) {
            lines.push(`    ${decl.instanceName} : ${decl.fbName};`);
        }
        for (const decl of sections.safetyDeclarations) {
            lines.push(`    ${decl}`);
        }
        lines.push("END_VAR");
        lines.push("");
    }
    lines.push(...sections.bodyLines);
    return lines.join("\n");
}
